import { useQuery } from "@tanstack/react-query";
import Spinner from "../../components/Shared/Spinner";
import ImgPlaceholder from "../../assets/ImgPlaceholder.svg";
import { useUserDataStore } from "../../stores/user";
import {
  getAdminProfile,
  getSalesProfile,
} from "../../api/Admins/GetAdminProfile";

const Field = ({ label, value }: { label: string; value?: string | null }) => (
  <div className="flex flex-col gap-1">
    <span className="text-sm font-medium text-lightGray">{label}</span>
    <span className="font-semibold text-burntBlack break-words">
      {value || "—"}
    </span>
  </div>
);

const AdminProfile = () => {
  const { userData } = useUserDataStore();
  const isSales = userData?.role === "sales";

  const { data, isLoading } = useQuery({
    // Sales records are id-scoped, admins read their own record from /admin/.
    queryFn: () =>
      isSales ? getSalesProfile(String(userData?.id)) : getAdminProfile(),
    queryKey: ["dashboardProfile", userData?.role, userData?.id],
    enabled: !isSales || !!userData?.id,
  });

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  // Fall back to the signed-in session data when the request fails, so the page
  // still shows who is logged in instead of an empty shell.
  const profile = data?.data ?? userData;
  const fullName = [profile?.firstName, profile?.lastName]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="py-8">
      <h1 className="mb-6 text-2xl font-bold text-burntBlack">My Profile</h1>

      <div className="p-6 bg-white border rounded-2xl border-black/15 max-w-[720px]">
        <div className="flex items-center gap-5 pb-6 mb-6 border-b border-black/10">
          <img
            src={profile?.img_url || ImgPlaceholder}
            className="object-cover border rounded-full size-20 border-black/20"
            alt=""
          />
          <div>
            <h2 className="text-xl font-bold capitalize text-burntBlack">
              {fullName || "—"}
            </h2>
            <p className="text-sm font-semibold text-primary">
              {isSales ? "Sales" : "Admin"}
            </p>
          </div>
        </div>

        <div className="grid gap-6 sm:grid-cols-2">
          <Field label="First Name" value={profile?.firstName} />
          <Field label="Last Name" value={profile?.lastName} />
          <Field label="Email" value={profile?.email} />
          <Field label="Phone" value={profile?.phone} />
        </div>

        {data?.error && (
          <p className="mt-6 text-sm font-medium text-red-500">
            Could not load the latest profile data ({data.error.message}).
            Showing your session details.
          </p>
        )}
      </div>
    </div>
  );
};

export default AdminProfile;
